/**
 * PlayerContactSockets (§1) — the shared source of truth for the physical contact
 * points on a player.
 *
 *   1. AUTHORITATIVE (sim): `computeSocket` derives each socket in world space from
 *      DETERMINISTIC sim state (player position, facing, and the swing phase of an
 *      action). The contact resolver uses these, so ball impulses stay a pure
 *      function of (state, inputs) — the server has no skeleton, so the sim can
 *      never depend on animated bone positions.
 * The client-side bone-name matcher that used to live here went with the
 * character reset: there are no animated bones to bind to any more. Only the
 * authoritative sim half remains, which is what ball contact was always
 * resolved from.
 *
 * Pure and deterministic (dcos/dsin only, no Math.random / clock), so it is safe
 * in /sim and unit-testable without a DOM.
 */
import { dcos, dsin } from '../math/vec3.js';
import type { WorldState } from './types.js';

export const enum ContactSocket {
  LeftFoot = 0,
  RightFoot = 1,
  LeftToe = 2,
  RightToe = 3,
  LeftTackle = 4,
  RightTackle = 5,
  Chest = 6,
  Head = 7,
  Pelvis = 8,
  BallControl = 9,
  /**
   * THE KEEPER'S GLOVES — where a gathered ball is actually held. (§4.5)
   *
   * Added because `stepHeldBall` claimed to "glue it to his hands" and then
   * computed a point from the CAPSULE: PLAYER_CAPSULE_RADIUS in front of the
   * centre line at PLAYER_CAPSULE_HEIGHT × 0.5, which is his waist. Rendered
   * close-up (MODELS_SHOWCASE 1100): the ball floats at belt height in front of
   * a keeper whose arms hang at his sides, touching nothing. The owner asked
   * *"make sure he really hold the ball and pick up"* and the honest answer was
   * no.
   *
   * Chest height and forward of the sternum is where a keeper carries a ball he
   * has gathered into his body — it is not a substitute for parenting the ball
   * to an animated hand BONE (that is a renderer change and is still owed), but
   * it puts the ball where the hands are in the gather pose instead of where
   * the capsule's midpoint happens to be.
   */
  Hands = 10,
}
export const CONTACT_SOCKET_COUNT = 11;

export const CONTACT_SOCKET_NAMES: readonly string[] = [
  'leftFoot', 'rightFoot', 'leftToe', 'rightToe', 'leftTackle', 'rightTackle',
  'chest', 'head', 'pelvis', 'ballControl', 'hands',
];

export interface SocketVec {
  x: number;
  y: number;
  z: number;
}

/**
 * Neutral local socket layout, metres, indexed by ContactSocket:
 * [forward, right, up] in the player's own frame (forward = facing, right =
 * facing rotated −90° about Y, up = world up). Sized to the ~1.8 m player the
 * renderer ships. Foot/toe forward offsets grow with the action swing (below).
 */
const OFFSET: readonly (readonly [number, number, number])[] = [
  [0.05, -0.11, 0.06], // LeftFoot
  [0.05, 0.11, 0.06], // RightFoot
  [0.20, -0.11, 0.04], // LeftToe
  [0.20, 0.11, 0.04], // RightToe
  [0.10, -0.14, 0.10], // LeftTackle
  [0.10, 0.14, 0.10], // RightTackle
  [0.10, 0.0, 1.35], // Chest
  [0.12, 0.0, 1.72], // Head
  [0.0, 0.0, 0.95], // Pelvis
  [0.55, 0.0, 0.05], // BallControl (just ahead of the feet, ground level)
  [0.40, 0.0, 1.18], // Hands (gloves, ball held clear of the sternum)
];

/** Extra forward reach (m) a foot/toe socket gains at full swing — the kicking or
 *  tackling foot reaches through the contact rather than sitting under the hips. */
const SWING_REACH = 0.45;

function isFoot(s: ContactSocket): boolean {
  return s === ContactSocket.LeftFoot || s === ContactSocket.RightFoot ||
    s === ContactSocket.LeftToe || s === ContactSocket.RightToe ||
    s === ContactSocket.LeftTackle || s === ContactSocket.RightTackle;
}

/**
 * World-space position of `socket` for player `id`, written into `out` (no alloc).
 * `swing` (0..1) is the action's normalized swing — a foot/toe socket reaches
 * further forward as it approaches the contact frame. Deterministic.
 */
export function computeSocket(
  state: WorldState,
  id: number,
  socket: ContactSocket,
  out: SocketVec,
  swing = 0,
): void {
  const p = state.players;
  const yaw = p.yaw[id]!;
  const fx = dcos(yaw);
  const fz = dsin(yaw);
  // Right = forward rotated −90° about Y (sim convention: forward=(cos,sin)).
  const rx = -fz;
  const rz = fx;
  const o = OFFSET[socket]!;
  const fwd = o[0] + (isFoot(socket) ? SWING_REACH * (swing < 0 ? 0 : swing > 1 ? 1 : swing) : 0);
  out.x = p.px[id]! + fx * fwd + rx * o[1];
  out.z = p.pz[id]! + fz * fwd + rz * o[1];
  out.y = p.py[id]! + o[2];
}

